import Player from '@vimeo/player';
import { addEventListener } from 'seng-disposable-event-listener';
import type { TimeData, VideoPlayerOptions } from './VideoPlayer.data';

export const placeholderVimeoUrl: string = 'https://vimeo.com/761508281/b9d66352f9'; // this is a blank-video for constructing vimeo-player -> should never be removed.

export default class VimeoPlayer {
  private readonly element: HTMLDivElement;
  private readonly options!: VideoPlayerOptions;
  private readonly player: typeof Player;
  private currentTime: number = 0;
  private secondsLoaded: number = 0;
  private duration: number = 0;
  private readonly clickInteractionEventListener!: () => void;
  private isPlaying: boolean = false;
  private attemptToPlay: boolean = false;

  public constructor(element: HTMLDivElement, options: VideoPlayerOptions) {
    this.element = element;
    this.options = options;
    const {
      playsinline,
      loop,
      muted,
      // controls,
      playing,
      url,
      onPlay,
      onReady,
      onProgress,
      onCreated,
    } = this.options;

    this.player = new Player(this.element, {
      playsinline,
      loop,
      muted,
      background: true,
      controls: false,
      autoplay: playing,
      autopause: false,
      dnt: true,
      id: url,
    });

    if (this.options.requireClickListener) {
      this.clickInteractionEventListener = addEventListener(
        window,
        'click',
        this.handleClickInteraction.bind(this),
        true,
      );
    }

    this.player.on('play', () => {
      this.refreshDuration();
      this.isPlaying = true;
      if (onPlay) onPlay();
    });

    if (onCreated) onCreated();

    this.player.on('loaded', () => {
      if (onReady) onReady();
      this.refreshDuration();

      if (!this.options.playing && !this.attemptToPlay) {
        this.pause();
      }
    });

    this.player
      .ready()
      .then(() => {
        const iframe = this.player.element;
        if (iframe) {
          iframe.style.width = '100%';
          iframe.style.height = '100%';
        }

        if (!this.options.playing && !this.attemptToPlay) {
          this.pause();
        }
      })
      .catch((error: unknown) => {
        console.warn(error);
      });

    this.player.on('pause', this.onPause.bind(this));
    // this.player.on('seeked', onSeek);
    this.player.on('ended', this.onEnded.bind(this));
    // this.player.on('error', (error) => {
    //   console.log(error)
    // });
    this.player.on('timeupdate', ({ seconds, duration, percent }: TimeData) => {
      this.currentTime = seconds;

      if (onProgress)
        onProgress({
          seconds,
          duration,
          percent,
        });
    });
    this.player.on('progress', ({ seconds }: TimeData) => {
      this.secondsLoaded = seconds;
    });
  }

  private onPause(): void {
    this.isPlaying = false;

    if (this.options.onPause) {
      this.options.onPause();
    }
  }

  private onEnded(): void {
    this.isPlaying = false;

    if (this.options.onEnded) {
      this.options.onEnded();
    }
  }

  private handleClickInteraction(): void {
    if (!this.isPlaying) {
      this.play();
      this.pause();
    }

    this.removeClickInteractionListener();
  }

  public removeClickInteractionListener(): void {
    if (this.clickInteractionEventListener) {
      this.clickInteractionEventListener();
    }
    // this.dispatchEvent(new VideoEvent(VideoEvent.types.CLICK_INTERACTION_CLICK, {}));
  }

  public async setVideo(url: string | undefined, autoplay = true): Promise<void> {
    if (url) {
      this.player
        .loadVideo(url)
        .then(() => {
          this.refreshDuration();

          if (autoplay) {
            this.play();
          } else {
            this.pause();
          }
        })
        .catch((error: unknown) => {
          console.warn(error);
        });
    }
  }

  private refreshDuration() {
    this.player
      ?.getDuration()
      ?.then((duration: number) => {
        if (this?.options.onDuration) {
          this.options.onDuration(duration);
        }
        this.duration = duration;
      })
      .catch((error: unknown) => {
        console.warn(error);
      });
  }

  public play(): Promise<void> {
    this.attemptToPlay = true;
    return this?.player?.play()?.catch((error: unknown) => {
      console.warn(error);
      // this.onPause();
    });
  }

  public pause(): Promise<void> {
    this.attemptToPlay = false;
    return this?.player?.pause()?.catch((error: unknown) => {
      console.warn(error);
    });
  }

  public setVolume(volume: number): Promise<void> {
    return this?.player?.setVolume(volume)?.catch((error: unknown) => {
      console.warn(error);
    });
  }

  public setMuted(muted: boolean): Promise<void> {
    return this?.player?.setMuted(muted)?.catch((error: unknown) => {
      console.warn(error);
    });
  }

  public seekTo(seconds: number): Promise<void> {
    return this?.player?.setCurrentTime(seconds)?.catch((error: unknown) => {
      console.warn(error);
    });
  }

  public getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public getInternalPlayer(): typeof Player {
    return this.player;
  }

  public getSecondsLoaded(): number {
    return this.secondsLoaded;
  }

  public dispose(): void {
    this.player.off('play');
    this.player.off('pause');
    this.player.off('seeked');
    this.player.off('error');
    this.player.off('ended');
    this.player.off('timeupdate');
    this.player.off('progress');
    this.player.destroy();
  }
}
